import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Globe2, ListChecks, Lock, Plus, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pager, SearchInput, ViewToggle } from "@/components/common/Layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebounced } from "@/hooks/useDirectory";
import { EmptyState, ErrorState, LoadingGrid, LoginPrompt } from "@/components/common/States";
import { formatDate, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

function NewListDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const submit = async () => {
    if (!name.trim()) return toast.error("Donnez un nom à la liste.");
    setSaving(true);
    try {
      const { data } = await api.post("/lists", { name, shared });
      toast.success(`Liste « ${data.name} » créée`);
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      navigate(`/listes/${data.id}`);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-list-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle liste</DialogTitle>
          <DialogDescription>Vous pourrez ensuite y ajouter des contacts depuis le répertoire Personnes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="list-name">Nom</Label>
            <Input id="list-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Relance partenaires 2027" data-testid="new-list-name-input" />
          </div>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
            <span><b>Partagée avec l'équipe</b><br /><span className="text-muted-foreground">Visible et modifiable par les autres membres.</span></span>
            <Switch checked={shared} onCheckedChange={setShared} data-testid="new-list-shared-switch" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="new-list-submit-button">{saving ? "Création…" : "Créer la liste"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Segment = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={cn("rounded-full px-4 py-1.5 text-sm font-semibold transition-colors", active ? "bg-primary text-white" : "text-stone-600 hover:bg-stone-100")}>
    {children}
  </button>
);

const PAGE_SIZE = 25;

export default function ListsPage() {
  const { isAuthed, loading } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const mine = sp.get("mine") === "1";
  const status = sp.get("status") || "active";
  const page = Math.max(1, Number(sp.get("page") || 1));
  const [text, setText] = useState(sp.get("q") || "");
  const q = useDebounced(text, 300);
  const [view, setView] = useState(() => localStorage.getItem("lm-lists-view") || "table");
  const changeView = (v) => { if (v) { localStorage.setItem("lm-lists-view", v); setView(v); } };
  const lists = useQuery({
    queryKey: ["lists", mine, status, q, page],
    queryFn: async () => (await api.get(`/lists?mine=${mine}&status=${status}&q=${encodeURIComponent(q)}&page=${page}&page_size=${PAGE_SIZE}`)).data,
    enabled: isAuthed,
    placeholderData: keepPreviousData,
  });
  const items = lists.data?.items || [];
  const update = (key, value) => setSp((prev) => { const n = new URLSearchParams(prev); value ? n.set(key, value) : n.delete(key); if (key !== "page") n.delete("page"); return n; });

  return (
    <div className="lm-container pb-20">
      <PageHeader eyebrow="Segmentation" title="Listes" icon={ListChecks} color="#EC7F13"
        description="Des regroupements opérationnels : invitations, relances, prospects… stockés dans Notion."
        actions={isAuthed && <Button className="rounded-full px-5 font-semibold" onClick={() => setCreating(true)} data-testid="create-list-button"><Plus className="h-4 w-4" /> Nouvelle liste</Button>} />
      {loading ? null : !isAuthed ? (
        <LoginPrompt title="Les listes sont réservées aux membres de La Mêlée" />
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="flex rounded-full border border-border bg-white p-1">
              <Segment active={!mine} onClick={() => update("mine", "")} testId="lists-filter-all">Toutes les listes</Segment>
              <Segment active={mine} onClick={() => update("mine", "1")} testId="lists-filter-mine">Mes listes</Segment>
            </div>
            <div className="flex rounded-full border border-border bg-white p-1">
              <Segment active={status === "active"} onClick={() => update("status", "")} testId="lists-status-active">Actives</Segment>
              <Segment active={status === "archived"} onClick={() => update("status", "archived")} testId="lists-status-archived">Archivées</Segment>
            </div>
            <SearchInput className="min-w-[220px] flex-1" value={text} onChange={(v) => { setText(v); update("page", ""); }} testId="lists-search-input" placeholder="Rechercher une liste…" />
            <ViewToggle value={view} onChange={changeView} />
          </div>
          {lists.isLoading ? <LoadingGrid /> : lists.isError ? <ErrorState onRetry={lists.refetch} /> : !items.length ? (
            <EmptyState icon={ListChecks} title="Aucune liste" testId="lists-empty-state"
              description="Créez une liste, ou sélectionnez des contacts dans le répertoire Personnes puis « Ajouter à une liste »."
              action={<Button asChild variant="outline" className="rounded-full"><Link to="/personnes">Ouvrir le répertoire</Link></Button>} />
          ) : view === "table" ? (
            <div className="lm-card overflow-hidden" data-testid="lists-table">
              <Table>
                <TableHeader><TableRow><TableHead>Liste</TableHead><TableHead>Visibilité</TableHead><TableHead>Créateur</TableHead><TableHead className="text-right">Contacts</TableHead><TableHead>Modifiée</TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((l) => (
                    <TableRow key={l.id} className="cursor-pointer" onClick={() => navigate(`/listes/${l.id}`)} data-testid={`list-row-${l.id}`}>
                      <TableCell><div className="font-semibold">{l.name}</div>{l.description && <div className="line-clamp-1 text-xs text-muted-foreground">{l.description}</div>}</TableCell>
                      <TableCell className="text-sm">{l.shared ? "Partagée" : "Privée"}</TableCell>
                      <TableCell className="text-sm text-stone-600">{l.is_mine ? "Moi" : l.creator_name || "—"}</TableCell>
                      <TableCell className="text-right font-bold text-[#EC7F13]">{formatNumber(l.members_count)}</TableCell>
                      <TableCell className="text-sm text-stone-500">{formatDate(l.last_edited_time)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="lists-grid">
              {items.map((l) => (
                <Link key={l.id} to={`/listes/${l.id}`} data-testid={`list-card-${l.id}`} className="lm-card lm-card-hover flex flex-col p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-bold leading-snug">{l.name}</h3>
                    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold", l.shared ? "bg-sky-50 text-sky-700" : "bg-stone-100 text-stone-600")}>
                      {l.shared ? <><Globe2 className="h-3 w-3" /> Partagée</> : <><Lock className="h-3 w-3" /> Privée</>}
                    </span>
                  </div>
                  <div className="mt-5 font-heading text-4xl font-extrabold text-[#EC7F13]">{formatNumber(l.members_count)}</div>
                  <div className="text-sm font-semibold text-stone-500">contact{l.members_count > 1 ? "s" : ""}</div>
                  <div className="mt-5 flex items-center justify-between border-t border-border/70 pt-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 truncate"><UserRound className="h-3 w-3" /> {l.is_mine ? "Moi" : l.creator_name || "—"}</span>
                    <span>Modifiée le {formatDate(l.last_edited_time)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <Pager page={page} total={lists.data?.total || 0} pageSize={PAGE_SIZE} onPage={(p) => update("page", p > 1 ? String(p) : "")} />
        </>
      )}
      <NewListDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
